import {Formik, Form, FormikHelpers} from 'formik'
import * as Yup from 'yup'
import FormikControl from '../../../components/formikControl/FormikControl'
import toast from 'react-hot-toast'
import {createBusBrand, updateBusBrand} from './api'
import {handleApiError} from '../../../helpers/errorHandler'
import {BusBrandBase, BusBrandFormValues, FormActionMap, FormType} from './type'
import {
  mapAPIDataToFormValues,
  mapFormDataToCreatePayload,
  mapFormDataToUpdatePayload,
} from './mapper'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'

function BusBrandForm({
  initialValues,
  onAfterSubmit,
}: {
  initialValues: BusBrandBase | undefined
  onAfterSubmit: () => void
}) {
  const formType: FormType = initialValues ? 'update' : 'add'

  const formActionMap: FormActionMap = {
    add: {
      action: createBusBrand,
      mapPayload: mapFormDataToCreatePayload,
    },
    update: {
      action: updateBusBrand,
      mapPayload: mapFormDataToUpdatePayload,
    },
  }

  const submitHandler = (
    values: BusBrandFormValues,
    {setSubmitting: setLoading}: FormikHelpers<BusBrandFormValues>
  ) => {
    const toastId = toast.loading('Submitting')
    setLoading(true)

    const executeAction = (actionPromise: Promise<any>) => {
      actionPromise
        .then(() => {
          toast.success('Success', {id: toastId})
          onAfterSubmit()
        })
        .catch((err) => {
          handleApiError(err, toastId)
        })
        .finally(() => {
          setLoading(false)
        })
    }

    if (formType === 'add') {
      const {action, mapPayload} = formActionMap.add
      const payload = mapPayload(values)
      const promise = action(payload)
      executeAction(promise)
    } else {
      const {action, mapPayload} = formActionMap.update
      const payload = mapPayload(values)
      const promise = action(payload)
      executeAction(promise)
    }
  }

  const formValues: BusBrandFormValues =
    formType === 'update'
      ? mapAPIDataToFormValues(initialValues!)
      : {busBrand: '', busBrandStatus: '0'}

  return (
    <div className="px-5 py-3">
      <Formik
        initialValues={formValues}
        validationSchema={Yup.object({
          busBrand: Yup.string()
            .max(100, 'The Bus Brand cannot exceed 100 characters')
            .required('Bus Brand is required'),
          busBrandStatus: Yup.string().required('Bus Brand Status is required'),
        })}
        onSubmit={submitHandler}
      >
        {({isSubmitting, resetForm, dirty, isValid}) => (
          <Form>
            <FormikControl
              control="input"
              required
              placeholder="Bus Brand"
              label="Bus Brand"
              name="busBrand"
            />
            <FormikControl
              control="check"
              type="radio"
              options={[
                {key: 'Active', value: '1'},
                {key: 'Inactive', value: '0'},
              ]}
              required
              placeholder="Bus Brand Status"
              label="Bus Brand Status"
              name="busBrandStatus"
            />
            <div className="text-center pt-10">
              <FormResetBtn onClick={() => resetForm} disabled={isSubmitting} className="me-4" />

              <FormSubmitBtn disabled={!dirty || !isValid || isSubmitting} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default BusBrandForm
